// pages/forms/formValidationMapping.js
//
// Single source of truth for "where does this publish-readiness/save error
// actually belong in the builder UI". The backend (services/
// formVersionService.js's validatePublishReadiness, and the field/scoring
// validators it composes) only ever returns { field_key, message, code }
// triples -- it has no concept of tabs, canvas selection, or which control
// to highlight. Every place in the builder that needs to turn one of those
// triples into "switch to this tab, select this field, highlight this
// control" calls into this module instead of re-deriving its own mapping
// (see the Form Builder UX task's explicit "do not scatter navigation
// logic across components" instruction).
//
// No react-router-dom (or any) import here -- kept dependency-free so it's
// unit-testable without the Jest/react-router-dom resolution issue that
// blocks importing FormBuilderPage.jsx directly (see src/App.test.js).

// Every tab id used by FormBuilderPage's <Tabs>/<Tab eventKey=...>.
export const TABS = {
  STRUCTURE: "structure",
  SCORING: "scoring",
  EXECUTION_PROFILE: "execution",
  PREVIEW: "preview",
};

// Every "which control" an issue can point at. Consumers (FormCanvas field
// cards, FormFieldEditor's Scoring Mode select, ScoringConfigEditor's grade
// band section, FormSectionEditor's weightage field) each only need to
// check whether their own TARGETS.* value is present for the field/section
// they're rendering.
export const TARGETS = {
  FIELD_TYPE: "field-type",
  SCORING_MODE: "scoring-mode",
  WEIGHTAGE: "weightage",
  GRADE_CONFIGURATION: "grade-configuration",
  EXECUTION_PROFILE: "execution-profile",
  GENERIC: "generic",
};

// The explicit code -> {tab, target} table (task §6). `field_key`/section
// membership is resolved separately in classifyIssue, since that requires
// the current `sections` state, but the tab/target for a given code is
// always the same regardless of which field/section it's about.
const CODE_MAP = {
  FORM_GRADE_CONFIGURATION_INVALID: { tab: TABS.SCORING, target: TARGETS.GRADE_CONFIGURATION },
  FORM_FIELD_TYPE_INVALID: { tab: TABS.STRUCTURE, target: TARGETS.FIELD_TYPE },
  FORM_SCORE_CONFIGURATION_INVALID: { tab: TABS.STRUCTURE, target: TARGETS.SCORING_MODE },
  FORM_FIELD_WEIGHTAGE_INVALID: { tab: TABS.STRUCTURE, target: TARGETS.WEIGHTAGE },
  FORM_EXECUTION_PROFILE_INVALID: { tab: TABS.EXECUTION_PROFILE, target: TARGETS.EXECUTION_PROFILE },
};

function findFieldLocation(sections, fieldKey) {
  if (!fieldKey) return { sectionIndex: null, fieldIndex: null, sectionId: null, fieldId: null };
  const sectionIndex = sections.findIndex((s) => (s.fields || []).some((f) => f.field_key === fieldKey));
  if (sectionIndex < 0) return { sectionIndex: null, fieldIndex: null, sectionId: null, fieldId: null };
  const fieldIndex = sections[sectionIndex].fields.findIndex((f) => f.field_key === fieldKey);
  return {
    sectionIndex,
    fieldIndex: fieldIndex >= 0 ? fieldIndex : null,
    sectionId: sections[sectionIndex].id,
    fieldId: fieldIndex >= 0 ? sections[sectionIndex].fields[fieldIndex].id : null,
  };
}

// A section-level weight error (services/formGradeService.js's
// validateWeightConfiguration) has field_key: null and instead names the
// section in its own message ("Section weightages must total 100" for the
// whole-section-weight case, or "Field weightages in section #<id> must
// total 100" for the per-field-within-section case) -- there's no
// structured sectionId on the error object itself, so it has to be parsed
// out of the one place it actually appears.
function extractSectionIdFromMessage(message) {
  const match = /section #(\d+)/i.exec(message || "");
  return match ? Number(match[1]) : null;
}

/**
 * Resolves one { field_key, message, code } publish-readiness/save error
 * into { tab, target, sectionIndex, fieldIndex, sectionId, fieldId }.
 * `sections` is the builder's current in-memory section/field array (used
 * only to resolve a field_key or a "section #<id>" message reference to a
 * canvas position -- never to change validation outcome).
 */
export function classifyIssue(error, sections = []) {
  const code = error.code || "";
  const message = error.message || "";

  const mapped = CODE_MAP[code];

  if (mapped?.tab === TABS.SCORING || /grade.?band|grade.config/i.test(message)) {
    return { tab: TABS.SCORING, target: TARGETS.GRADE_CONFIGURATION, sectionIndex: null, fieldIndex: null, sectionId: null, fieldId: null };
  }

  if (mapped?.tab === TABS.EXECUTION_PROFILE) {
    return { tab: TABS.EXECUTION_PROFILE, target: TARGETS.EXECUTION_PROFILE, sectionIndex: null, fieldIndex: null, sectionId: null, fieldId: null };
  }

  // Weightage errors: either per-field (has field_key) or whole-section /
  // per-section-fields (field_key is null, section referenced by message).
  if (mapped?.target === TARGETS.WEIGHTAGE || /weightage/i.test(message)) {
    if (error.field_key) {
      const loc = findFieldLocation(sections, error.field_key);
      return { tab: TABS.STRUCTURE, target: TARGETS.WEIGHTAGE, ...loc };
    }
    const sectionId = extractSectionIdFromMessage(message);
    const sectionIndex = sectionId != null ? sections.findIndex((s) => s.id === sectionId) : (
      // "Section weightages must total 100" (no #id) means the whole-
      // section weighted-sections mode -- surface it on the first
      // section that actually has score_enabled+weightage set, since
      // that's the config the message is about.
      sections.findIndex((s) => s.score_enabled && s.weightage != null)
    );
    return {
      tab: TABS.STRUCTURE,
      target: TARGETS.WEIGHTAGE,
      sectionIndex: sectionIndex >= 0 ? sectionIndex : null,
      fieldIndex: null,
      sectionId: sectionIndex >= 0 ? sections[sectionIndex].id : sectionId,
      fieldId: null,
    };
  }

  if (error.field_key) {
    const loc = findFieldLocation(sections, error.field_key);
    const target = mapped?.target || (/field_type|not supported/i.test(message) ? TARGETS.FIELD_TYPE : /scoring_mode/i.test(message) ? TARGETS.SCORING_MODE : TARGETS.GENERIC);
    return { tab: TABS.STRUCTURE, target, ...loc };
  }

  return { tab: TABS.STRUCTURE, target: TARGETS.GENERIC, sectionIndex: null, fieldIndex: null, sectionId: null, fieldId: null };
}

/**
 * Groups a validation.errors[] array by tab, returning
 * { structure: [...], scoring: [...], execution: [...] } (each array holds
 * the original error objects, in order) -- used for both the tab error-
 * count badges and the top issue panel's "group errors by tab" display.
 */
export function groupErrorsByTab(errors, sections = []) {
  const groups = { [TABS.STRUCTURE]: [], [TABS.SCORING]: [], [TABS.EXECUTION_PROFILE]: [] };
  for (const error of errors || []) {
    const { tab } = classifyIssue(error, sections);
    if (!groups[tab]) groups[tab] = [];
    groups[tab].push(error);
  }
  return groups;
}

/**
 * Builds a field_key -> error lookup for every error classifyIssue can pin
 * to a specific field (field-type/scoring-mode/per-field-weightage) -- used
 * by FormCanvas to show the inline red-card state and by
 * FormFieldPropertiesPanel to show the invalid-control state on whichever
 * field is currently selected.
 */
export function buildFieldErrorMap(errors, sections = []) {
  const map = {};
  for (const error of errors || []) {
    if (!error.field_key) continue;
    const { target } = classifyIssue(error, sections);
    if (target === TARGETS.GRADE_CONFIGURATION || target === TARGETS.EXECUTION_PROFILE) continue;
    if (!map[error.field_key]) map[error.field_key] = [];
    map[error.field_key].push({ ...error, target });
  }
  return map;
}
